import React from 'react';

import {useTranslation} from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";

import {formatDateToLocale} from "../../utils";
import {ApplicationDetailsType} from "../types";
import {OAuthDataProps} from "../../../../context/type";

import AlertStepSkipped from "./AlertStepSkipped";
import {path} from "../../../Utils/const";
import File from "../../../Utils/File";
import ZoomPanRotateImage from "../ImageZoom/ZoomPanRotateImage";
import BaseDataContent from "./BaseDataContent";
import FadeInDetailsContent from "../ImageZoom/FadeInDetailsContent";
import DetailsInfoRow from "./DetailsInfoRow";

type DetailsContentProps = {
    details: ApplicationDetailsType
    oauthData: OAuthDataProps
}

const DetailsContent: React.FC<DetailsContentProps> = ({details, oauthData}) => {
    const {t} = useTranslation('common');

    const {baseData, identificationDetails, recruitSignature, businessInformation, bankDetails, mandate} = details;
    console.log({bankDetails});

    return (
        <Box sx={{mt: 5, mb: 5}}>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                <Box sx={{pr: 1}}>
                    <Box>
                        <BaseDataContent baseData={baseData} recruitEmail={details.recruitEmail} mandate={mandate}/>
                        <Grid container position="relative" zIndex={4}>
                            <Grid xs={2} item/>
                            <Grid item xs={10}>
                                <FadeInDetailsContent target={window} threshold={450} baseData={baseData}
                                                      recruitEmail={details.recruitEmail} style={{top: 120}}
                                                      mandate={mandate}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{mt: 4}}>
                        {businessInformation !== null ? <Stack spacing={1}>
                                <Typography variant="body1">{t("registered_as")}<br/>
                                    <b>{t(businessInformation?.recruitContractType)}</b></Typography>
                                <Typography variant="body1">{t("small_business_owner")}<br/>
                                    <b>{t(businessInformation?.recruitBusinessRegulation)}</b></Typography>
                                {businessInformation?.recruitBusinessRegulation === "no" &&
                                    <>
                                        <Typography variant="body1">{t("tax_number")}<br/>
                                            <b>{businessInformation?.recruitTaxNumber}</b></Typography>
                                        <Typography variant="body1">{(t("ust_idnr"))}<br/>
                                            <b>{businessInformation?.recruitVatNumber}</b></Typography>
                                        <Typography variant="body1">{t("tax_office")}<br/>
                                            <b>{businessInformation?.recruitTaxOffice}</b></Typography>
                                    </>
                                }
                            {mandate === 4 && baseData.recruitIsCompany && <Typography variant="body1">{t("company_registration_number")}<br/>
                                    <b>{businessInformation?.recruitCompanyRegistrationNumber}</b></Typography>}
                            </Stack>
                            : <AlertStepSkipped label={t("step_skipped_business_info")}/>}
                    </Box>

                    <Box sx={{mt: 4}}>
                        <Stack spacing={1}>
                            <DetailsInfoRow
                                title={t("application_shopify_contract")}
                                value={details?.recruitShopifyContractConfirmed ? t("yes") : t("no")}
                            />
                        </Stack>
                    </Box>
                </Box>

                <Box sx={{pr: 1}}>
                    {identificationDetails !== null && path
                        ?
                        <Stack spacing={3}>
                            {identificationDetails?.recruitIdentityCardFront && <ZoomPanRotateImage
                                src={`${path}${identificationDetails?.recruitIdentityCardFront?.endpoint}`}
                                baseData={baseData} mandate={mandate} recruitEmail={details.recruitEmail}/>}
                            {identificationDetails?.recruitIdentityCardBack && <ZoomPanRotateImage
                                src={`${path}${identificationDetails?.recruitIdentityCardBack?.endpoint}`}
                                baseData={baseData} mandate={mandate} recruitEmail={details.recruitEmail}/>}
                            {identificationDetails?.recruitBusinessRegistration &&
                                <File url={path + identificationDetails?.recruitBusinessRegistration?.endpoint}
                                      title={"Gewerbeanmeldung"} recruitEmail={details.recruitEmail} baseData={baseData}
                                      mandate={mandate}/>}
                            {identificationDetails?.recruitOtherVerification &&
                                <File url={path + identificationDetails?.recruitOtherVerification?.endpoint}
                                      title={"Sonstiger Nachweis"} recruitEmail={details.recruitEmail}
                                      baseData={baseData} mandate={mandate}/>}
                            {recruitSignature && <ZoomPanRotateImage src={`${path}${recruitSignature?.endpoint}`}
                                                                     styles={{backgroundColor: "rgba(0, 0, 0, 0.08)"}}
                                                                     type="signature" mandate={mandate}/>}
                        </Stack>
                        : <AlertStepSkipped label={t("step_skipped_identification")}/>}
                </Box>

                <Box>
                    {bankDetails !== null ? <>
                            <Typography variant="body1"
                                        sx={{mb: 3}}>{bankDetails?.isSepa ? `${t("sepa")} & ${t("commission")}` : t("commission")}</Typography>
                            <Box sx={{mb: 3}}>
                                <Typography variant="body1">{bankDetails?.accountOwnerCompany}</Typography>
                                <Typography
                                    variant="body1">{bankDetails?.accountOwnerFirstName} {bankDetails?.accountOwnerLastName}</Typography>
                                {!bankDetails?.accountOwnerStreetHouseNumber && <Typography
                                    variant="body1">{bankDetails?.accountOwnerStreet} {bankDetails?.accountOwnerHouseNumber}</Typography>}
                                {bankDetails?.accountOwnerStreetHouseNumber && <Typography
                                    variant="body1">{bankDetails?.accountOwnerStreetHouseNumber}</Typography>}
                                <Typography
                                    variant="body1">{bankDetails?.accountOwnerPostCode} {bankDetails?.accountOwnerCity}</Typography>
                            </Box>
                            <Box sx={{mb: 3}}>
                                <Typography variant="body1">{bankDetails?.iban}</Typography>
                                <Typography variant="body1">{bankDetails?.bic}</Typography>
                                <Typography variant="body1">{bankDetails?.bankName}</Typography>
                            </Box>
                        </>
                        : <AlertStepSkipped label={t("step_skipped_bank")}/>}

                    {bankDetails?.isSepa && bankDetails?.accountOwnerSignatureDate && bankDetails.accountOwnerSignature !== null && path
                        ? <Box>
                            <Typography
                                sx={{mb: 3}}>{formatDateToLocale(bankDetails?.accountOwnerSignatureDate, oauthData)}, {bankDetails?.accountOwnerCity}</Typography>
                            <ZoomPanRotateImage
                                src={`${path}${bankDetails.accountOwnerSignature?.endpoint}`}
                                styles={{backgroundColor: "rgba(0, 0, 0, 0.08)"}}
                                type="signature"
                                mandate={mandate}
                            />
                        </Box>
                        : null}
                </Box>

            </Box>

        </Box>
    );
};

export default DetailsContent;